// MeetingRoomCard.jsx - Updated with gallery functionality and new layout
import React, { useState } from 'react';
import { Box, Typography, Card, CardContent, Chip, IconButton, Tooltip } from '@mui/material';
import { styled } from '@mui/material/styles';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { API_BASE_URL } from 'config/api';
import * as Icons from '@mui/icons-material';

// Import the AvailabilityBar component
import AvailabilityBar from './availabilityBar';

// Styled components
const MeetingRoomCard = styled(Card)(({ theme }) => ({
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  margin: 0, // Remove margin
  transition: 'all 0.2s ease-in-out',
  cursor: 'pointer',
  height: '100%', // Ensure consistent height
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: theme.shadows[4],
  }
}));

const ResourceCardContent = styled(CardContent)(({ theme }) => ({
  padding: theme.spacing(1.5),
  '&:last-child': {
    paddingBottom: theme.spacing(1.5),
  },
  display: 'flex',
  flexDirection: 'column',
  flexGrow: 1, // Allow content to grow to fill the card
}));

const ResourceImage = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.grey[100],
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  borderTopLeftRadius: theme.shape.borderRadius,
  borderTopRightRadius: theme.shape.borderRadius,
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  aspectRatio: '16/9', // Fixed aspect ratio for all images
}));

const NavigationButton = styled(IconButton)(({ theme }) => ({
  backgroundColor: 'rgba(255, 255, 255, 0.7)',
  color: theme.palette.grey[800],
  position: 'absolute',
  padding: 8,
  '&:hover': {
    backgroundColor: 'rgba(255, 255, 255, 0.9)',
  },
  zIndex: 1,
}));

const ResourceCapacity = styled(Chip)(({ theme }) => ({
  position: 'absolute',
  top: theme.spacing(1),
  right: theme.spacing(1),
  backgroundColor: 'rgba(255, 255, 255, 0.85)',
  fontSize: '0.75rem',
  height: '24px',
  '& .MuiChip-icon': {
    fontSize: '1rem',
  }
}));

const ResourceTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 600,
  fontSize: '1.1rem',
  marginBottom: theme.spacing(0.5),
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
}));

const PriceChips = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  gap: theme.spacing(0.75),
  marginBottom: theme.spacing(1.5),
}));

const AmenityIcon = styled(Box)(({ theme, index }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 28,
  height: 28,
  borderRadius: '50%',
  backgroundColor: theme.palette.action.hover,
  transition: 'all 0.2s',
  '&:hover': {
    backgroundColor: theme.palette.action.selected,
  },
  marginRight: theme.spacing(0.5),
}));

const AmenitiesContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  marginTop: 'auto', // Push to bottom of card
  height: 32, // Space for two rows of icons
  alignItems: 'flex-start',
}));

const getIconNameFromService = (serviceName) => {
  // Map common service names to appropriate Material-UI icon names
  const serviceIconMap = {
    // Access
    "24/7 Access": "AccessTime",
    "24 Hour Access": "AccessTime",
    "Access": "VpnKey",
    
    // Seating & Furniture
    "Comfortable Seating": "Weekend",
    "Ergonomic Chairs": "Chair",
    "Standing Desks": "TableBar",
    
    // Amenities
    "Pet Friendly": "Pets",
    "Air Conditioning": "AcUnit",
    "Heating": "Fireplace",
    "Kitchen": "Kitchen",
    "Coffee": "Coffee",
    "Tea": "EmojiFoodBeverage",
    "Water": "Water",
    "Snacks": "LocalCafe",
    
    // Tech
    "High Speed Internet": "Wifi",
    "WiFi": "Wifi",
    "Projector": "VideoSettings",
    "TV": "Tv",
    "Video Conferencing": "Videocam",
    "Whiteboard": "Edit",
    
    // Services
    "On-Site Support": "SupportAgent",
    "Reception": "RecordVoiceOver",
    "Cleaning": "CleaningServices",
    "Mail Handling": "Mail",
    "Printing": "Print",
    "Security": "Security",
    
    // Facilities
    "Bike Storage": "DirectionsBike",
    "Parking": "LocalParking",
    "Shower": "Shower",
    "Restrooms": "Wc",
    "Accessibility": "Accessible",
    
    // Default
    "default": "CheckCircle"
  };
  


  // Normalize the service name for comparison (remove spaces, lowercase)
  const normalizedName = serviceName
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim();
  
  // Try to find an exact match
  for (const [key, value] of Object.entries(serviceIconMap)) {
    if (key.toLowerCase() === normalizedName) {
      return value;
    }
  }
  
  // If no exact match, try to find a partial match
  for (const [key, value] of Object.entries(serviceIconMap)) {
    if (normalizedName.includes(key.toLowerCase())) {
      return value;
    }
  }
  
  // If all else fails, return the default icon
  return serviceIconMap.default;
};

const MeetingRoomCardComponent = ({ room, onClick, isMobile, theme, onSelectRoom, isSelected = false }) => {
  // State for gallery navigation
  const [activeStep, setActiveStep] = useState(0);
  
  // Handle gallery images
  const images = room.images || [];
  // Fallback to single image if gallery is not available
  if (images.length === 0 && room.image) {
    images.push({ url: room.image, name: room.name });
  }
  
  const maxSteps = images.length;

  const getIconComponent = (iconName) => {
    return Icons[iconName] || Icons.Help;
  };

  // Gallery navigation handlers
  const handleNext = (e) => {
    e.stopPropagation(); // Prevent the card's onClick from firing
    setActiveStep((prevStep) => (prevStep + 1) % maxSteps);
  };

  const handlePrev = (e) => {
    e.stopPropagation(); // Prevent the card's onClick from firing
    setActiveStep((prevStep) => (prevStep - 1 + maxSteps) % maxSteps);
  };

  // Sample features for testing - these would come from the room.features in production
  const services = room.services || [];

  // Get pricing data
  const hourlyRate = room.hourly_rate;
  const fullDayPrice = room.full_day_price;
  const morningHalfDayPrice = room.morning_half_day_price;
  const afternoonHalfDayPrice = room.afternoon_half_day_price;
  
  // Use morning half day price if it's available, otherwise afternoon
  const halfDayPrice = morningHalfDayPrice || afternoonHalfDayPrice;

  return (
    <MeetingRoomCard onClick={() => onClick(room)}>
      <ResourceImage>
        {/* Navigation buttons - only show if there are multiple images */}
        {maxSteps > 1 && (
          <>
            <NavigationButton 
              size="small" 
              onClick={handlePrev}
              sx={{ left: 8 }}
            >
              <ChevronLeftIcon fontSize="small" />
            </NavigationButton>
            
            <NavigationButton 
              size="small" 
              onClick={handleNext}
              sx={{ right: 8 }}
            >
              <ChevronRightIcon fontSize="small" />
            </NavigationButton>
          </>
        )}
        
        {/* Image */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundImage: images[activeStep]?.url 
              ? `url(${API_BASE_URL}${images[activeStep].url})`
              : 'none',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        />
        
        <ResourceCapacity
          label={`${room.capacity || 1} ${room.capacity === 1 ? 'person' : 'people'}`}
          size="small"
        />
      </ResourceImage>
      <ResourceCardContent>
        <ResourceTitle>{room.name}</ResourceTitle>
        
        {/* Price chips */}
        <PriceChips>
          {/* Hourly rate - most prominent */}
          {hourlyRate && (
            <Chip 
              size="small"
              label={`£${hourlyRate}/hour`}
              color="primary"
              sx={{ fontWeight: 'bold' }}
            />
          )}
          
          {/* Half day price */}
          {halfDayPrice && (
            <Chip 
              size="small"
              label={`£${halfDayPrice} half day`}
              variant="outlined"
            />
          )}
          
          {/* Full day price */}
          {fullDayPrice && (
            <Chip 
              size="small"
              label={`£${fullDayPrice} full day`}
              variant="outlined"
            />
          )}
        </PriceChips>
        
        {/* Amenities */}
        <AmenitiesContainer>
          {services && services.length > 0 ? (
            services.map((service, index) => {
              // Convert service name to a likely icon name
              // This is a simple conversion - you may need to map specific services to icons
              const iconName = getIconNameFromService(service.name);
              const IconComponent = getIconComponent(iconName);
              
              return (
                <Tooltip
                  key={service.id || `service-${index}`}
                  title={service.description || service.name || ''}>
                  <AmenityIcon index={index}>
                    <IconComponent
                      sx={{
                        fontSize: '1.2rem',
                        color: theme.palette.text.primary
                      }}
                    />
                  </AmenityIcon>
                </Tooltip>
              );
            })
          ) : (
            <Typography variant="caption" color="text.secondary">
              No amenities listed
            </Typography>
          )}
        </AmenitiesContainer>
      </ResourceCardContent>
    </MeetingRoomCard>
  );
};

export default MeetingRoomCardComponent;